// Two distinct things people often conflate — written as copy, not description.
// Ally = the AI assistant working alongside each specialist.
// The Vault = the software where the work, the records, and the reports live.
import { motion } from "framer-motion";
import { Bot, Database } from "lucide-react";

const ITEMS = [
  {
    key: "ally",
    label: "Ally",
    title: "Knows your playbook. Whispers it back when it counts.",
    body:
      "Ally rides shotgun with every specialist — surfacing the right checklist, flagging the next move, catching the slip before it ships. Your best operator's instincts, running on every desk.",
    Icon: Bot,
  },
  {
    key: "vault",
    label: "The Vault",
    title: "The work goes in. The numbers come out.",
    body:
      "Cases, records, and reports live in one place — even the bits pulled from legacy systems that don't talk to anything else. Stop chasing dashboards. They'll find you.",
    Icon: Database,
  },
];

export default function AllyAndVault() {
  return (
    <section
      id="ally-vault"
      className="relative bg-[#ffffff] border-t border-black/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12 md:mb-16">
          <div className="md:col-span-3">
            <div className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.3em] text-black/65">
              <span className="eyebrow-bar" aria-hidden="true" />
              <span>Built into every engagement</span>
            </div>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-tightest max-w-4xl text-[#0a0a0a]">
              Meet Ally. Meet the Vault.
              <span className="block italic font-light text-black/65">
                Two tools doing the heavy lifting.
              </span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          {ITEMS.map((it, idx) => {
            const Icon = it.Icon;
            return (
              <motion.div
                key={it.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.1 }}
                className="bg-[#ffffff] p-8 md:p-12 lg:p-14 flex flex-col gap-6"
              >
                <div className="inline-flex items-center justify-center h-12 w-12 border border-black/25 text-[#0a0a0a]">
                  <Icon size={20} strokeWidth={1.4} />
                </div>
                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-black/65">
                  {it.label}
                </div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-tightest text-[#0a0a0a] max-w-lg">
                  {it.title}
                </h3>
                <p className="font-sans text-black/75 text-base md:text-lg leading-relaxed max-w-lg">
                  {it.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
